"use client";

/**
 * R3-33 S4-D-2 Phase D-6 — @닉네임 자동완성 textarea.
 *
 * 동작:
 * - textarea 안에서 `@` 입력 감지 → caret 이전의 partial nickname 추출
 * - GET /api/community/mention-suggest 로 친구·컨텍스트 우선 추천
 * - drop-down 표시 → ↑↓/Enter/Tab 으로 선택, Esc 로 닫음, 클릭으로 선택
 * - 선택 시 `@partial` → `@nickname ` (공백 1) 으로 치환
 *
 * Props:
 * - value, onChange: 일반 textarea 와 동일
 * - contextPostId: 같은 글 댓글 단 사람 우선 정렬 (optional)
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "@/store/auth";

interface Suggestion {
  user_id: number;
  nickname: string;
  is_friend: boolean;
  in_context: boolean;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  contextPostId?: number | null;
  className?: string;
  disabled?: boolean;
}

// "@partial" 추출용 — caret 직전의 마지막 @토큰
const MENTION_PROBE = /@([\w가-힣]{0,29})$/;

export function MentionTextarea({
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
  contextPostId,
  className,
  disabled,
}: Props) {
  const { token } = useAuthStore();
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [probeStart, setProbeStart] = useState<number>(-1);    // @ 위치 (자동완성 활성 시)
  const fetchSeqRef = useRef(0);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setSuggestions([]);
    setProbeStart(-1);
    setActiveIdx(0);
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!token) return;
    const seq = ++fetchSeqRef.current;
    try {
      const r = await axios.get<Suggestion[]>("/api/community/mention-suggest", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          q,
          limit: 8,
          ...(contextPostId ? { context_post_id: contextPostId } : {}),
        },
      });
      if (seq !== fetchSeqRef.current) return;  // stale 응답 폐기
      setSuggestions(r.data);
      setActiveIdx(0);
      setOpen(r.data.length > 0);
    } catch {
      if (seq === fetchSeqRef.current) closeDropdown();
    }
  }, [token, contextPostId, closeDropdown]);

  // value/caret 변경 시 @probe 검사
  const checkProbe = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const m = MENTION_PROBE.exec(before);
    if (m) {
      setProbeStart(caret - m[0].length);
      fetchSuggestions(m[1]);
    } else {
      closeDropdown();
    }
  }, [value, fetchSuggestions, closeDropdown]);

  // 외부 value 변경 시에도 caret 다음 frame 체크
  useEffect(() => {
    if (!open) return;
    checkProbe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const insertNickname = (nick: string) => {
    const ta = taRef.current;
    if (!ta || probeStart < 0) return;
    const caret = ta.selectionStart ?? value.length;
    const next = value.slice(0, probeStart) + `@${nick} ` + value.slice(caret);
    onChange(next);
    closeDropdown();
    // caret 위치 보정
    requestAnimationFrame(() => {
      if (ta) {
        const newCaret = probeStart + nick.length + 2;  // @nick + 공백
        ta.focus();
        ta.setSelectionRange(newCaret, newCaret);
      }
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const pick = suggestions[activeIdx];
      if (pick) insertNickname(pick.nickname);
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyUp={checkProbe}
        onClick={checkProbe}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        className={className}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-bg-elevated border border-border rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
          <p className="text-2xs text-text-sub px-3 py-1.5 border-b border-border">
            💡 ↑↓ 이동 · Enter/Tab 선택 · Esc 닫기
          </p>
          {suggestions.map((s, i) => (
            <button
              key={s.user_id}
              type="button"
              onClick={() => insertNickname(s.nickname)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                i === activeIdx ? "bg-primary/15" : "hover:bg-bg/50"
              }`}
            >
              <span className="text-sm text-text font-bold flex-1 truncate">
                @{s.nickname}
              </span>
              {s.is_friend && (
                <span className="text-2xs text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                  친구
                </span>
              )}
              {!s.is_friend && s.in_context && (
                <span className="text-2xs text-text-sub bg-bg-elevated px-1.5 py-0.5 rounded shrink-0">
                  댓글 단 사람
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
